import React, { lazy, Suspense } from "react";
import ProtectedRoute from "components/ProtectedRoute";
import Loader from "components/Loader";
import authentication from "components/react-azure-b2c.js";
import { AppState, OutageModel } from "store/types";
import { Dispatch } from "redux";
import SubmiticaOutage from "containers/OutagePage";
import { LinkActionResponse } from "common/enums";
import NotFound from "containers/NotFound";
import { useSelector } from "react-redux";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

const Application = lazy(() => import("containers/Application"));

const ViewApplications = lazy(() => import("containers/ViewApplications"));
const ApplicationDetail = lazy(() => import("containers/ApplicationDetail"));
const CreateApplication = lazy(() => import("containers/CreateApplication"));
const ManageAccount = lazy(() => import("containers/ManageAccount"));
const PlanningMap = lazy(() => import("containers/PlanningMap"));
const BillPayerDetail = lazy(() => import("containers/BillPayerDetail"));
const BillPayers = lazy(() => import("containers/ViewBillPayers"));
const UserNotifications = lazy(() => import("containers/UserNotifications"));
const ViewInvoices = lazy(() => import("containers/ViewInvoices"));
const InvoiceDetail = lazy(() => import("containers/InvoiceDetail"));
const UserLinkActionResponse = lazy(() =>
  import("containers/UserLinkActionResponse")
);
const Support = lazy(() => import("containers/Support"));
const SubmiticaVideoGuides = lazy(() =>
  import("containers/SubmiticaVideoGuides")
);
const Policy = lazy(() => import("containers/Policy"));
const Announcements = lazy(() => import("containers/Announcements"));

const ViewApplicationsElement = authentication.required(ViewApplications);
const ApplicationDetailElement = authentication.required(ApplicationDetail);
const CreateApplicationElement = authentication.required(CreateApplication);
const ManageAccountElement = authentication.required(ManageAccount);
const PlanningMapElement = authentication.required(PlanningMap);
const BillPayerDetailElement = authentication.required(BillPayerDetail);
const BillPayersElement = authentication.required(BillPayers);
const UserNotificationsElement = authentication.required(UserNotifications);
const ViewInvoicesElement = authentication.required(ViewInvoices);
const InvoiceDetailElement = authentication.required(InvoiceDetail);
const UserLinkActionResponseElement = authentication.required(
  UserLinkActionResponse
);
const SupportElement = authentication.required(Support);
const SubmiticaVideoGuidesElement = authentication.required(
  SubmiticaVideoGuides
);
const PolicyElement = authentication.required(Policy);
const AnnouncementsElement = authentication.required(Announcements);

type RoutesProps = {
  outageModel?: OutageModel;
};

const ApplicationLoader = () => (
  <Suspense fallback={<Loader />}>
    <Application />
  </Suspense>
);

const Routes = (props: RoutesProps) => {
  const { isLoggedIn } = useSelector((state: AppState) => state.auth);

  const allowBypass =
    window.location.pathname.toLowerCase().indexOf("response") > -1;

  const ApplicationLoaderElement = authentication.required(ApplicationLoader);

  const router = () => {
    {
      /* for local testing, use 'ap'. Change to 'api' once done enhancement.
      This implementation should be changed once S1 is decomissioned.
    */
    }

    if (props.outageModel && props.outageModel!.isSubmiticaOffline) {
      return createBrowserRouter([
        {
          path: "/",
          element: (
            <SubmiticaOutage outageMessage={props.outageModel!.outageMessage} />
          )
        }
      ]);
    } else {
      return createBrowserRouter([
        {
          path: "/",
          element: (
            <ProtectedRoute>
              {!isLoggedIn && !!allowBypass ? (
                <ApplicationLoader />
              ) : (
                <ApplicationLoaderElement />
              )}
            </ProtectedRoute>
          ),
          children: [
            {
              path: "/",
              element: <ViewApplicationsElement />
            },
            {
              path: "/applicationdetail/:carId",
              element: <ApplicationDetailElement />
            },
            {
              path: "/response",
              element: <ApplicationDetail />
            },
            {
              path: "/billpayerdetail/:billPayerId",
              element: <BillPayerDetailElement />
            },
            {
              path: "/invoice/:invoiceId",
              element: <InvoiceDetailElement />
            },
            {
              path: "/create",
              element: <CreateApplicationElement />
            },
            {
              path: "/manageaccount",
              element: <ManageAccountElement />
            },
            {
              path: "/viewaccount/:userId",
              element: <ManageAccountElement />
            },
            {
              path: "/updateaccount",
              element: <ManageAccount isUpdateUserDetail={true} />
            },
            {
              path: "/account/confirmuserlink",
              element: <ManageAccountElement />
            },
            {
              path: "/account/declineuserlink",
              element: <ManageAccountElement />
            },
            {
              path: "/declineaddparticipant",
              element: (
                <UserLinkActionResponse
                  userLinkAction={LinkActionResponse.DeclineParticipant}
                />
              )
            },
            {
              path: "/planningmap",
              element: <PlanningMapElement />
            },
            {
              path: "/billpayers",
              element: <BillPayersElement />
            },
            {
              path: "/notifications",
              element: <UserNotificationsElement />
            },
            {
              path: "/invoices",
              element: <ViewInvoicesElement />
            },
            {
              path: "/support",
              element: <SupportElement />
            },
            {
              path: "/submiticavideoguides",
              element: <SubmiticaVideoGuidesElement />
            },
            {
              path: "/policy/:policyType",
              element: <PolicyElement />
            },
            {
              path: "/announcements",
              element: <AnnouncementsElement />
            },
            {
              path: "*",
              element: <NotFound />
            }
          ]
        }
      ]);
    }
  };

  return <RouterProvider router={router()} />;
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {};
};

export default Routes;
